<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { getRecentViews, type EntityViewLogResponse } from '@/api/modules/loggingApi';
import { formatTaxId, formatDate } from '@/utils/formatters';

const props = defineProps<{
    entityType?: 'company' | 'person' | 'report' | 'risk_profile';
    title?: string;
    limit?: number;
}>();

const recentItems = ref<EntityViewLogResponse[]>([]);
const loading = ref(true);

const fetchRecent = async () => {
    loading.value = true;
    try {
        const response = await getRecentViews(props.entityType, props.limit || 5);
        // Filter out items without entity_id to prevent router errors
        recentItems.value = response.filter(item => item.entity_id);
    } catch (error) {
        console.error('Failed to fetch recent activity:', error);
    } finally {
        loading.value = false;
    }
};

onMounted(fetchRecent);

const getIcon = (type: string) => {
    switch (type) {
        case 'company': return 'business';
        case 'person': return 'person';
        case 'report': return 'description';
        case 'risk_profile': return 'shield';
        default: return 'history';
    }
};

const getRoute = (item: EntityViewLogResponse) => {
    // Guard against missing entity_id to prevent router errors
    if (!item.entity_id) {
        return { name: 'home' };
    }

    switch (item.entity_type) {
        case 'company':
            return { name: 'company-general-headquarters', params: { cnpj: item.entity_id } };
        case 'person':
            // Person route expects uuid.
            // Note: If we logged TaxID as entity_id, we need the UUID here.
            // This might need a refinement if we don't store UUID in EntityViewLogResponse.
            return { name: 'person-general-data', params: { uuid: item.entity_id } };
        default:
            return { name: 'home' };
    }
};

const formatTime = (dateStr: string) => {
    return formatDate(dateStr, { hour: '2-digit', minute: '2-digit' }, '');
};

const showTypeIcon = computed(() => !props.entityType);

</script>

<template>
    <div class="recent-activity-card">
        <h3 v-if="title" class="title">{{ title }}</h3>

        <div v-if="loading" class="loading-state">
            <span class="material-icons spin">refresh</span>
        </div>

        <div v-else-if="recentItems.length === 0" class="empty-state">
            <p>Nenhuma atividade recente.</p>
        </div>

        <ul v-else class="activity-list">
            <li v-for="item in recentItems" :key="item.id" class="activity-item">
                <router-link :to="getRoute(item)" class="activity-link">
                    <div class="icon-wrapper" :class="item.entity_type">
                        <span class="material-icons">{{ getIcon(item.entity_type) }}</span>
                    </div>
                    <div class="info flex-grow">
                        <div class="flex justify-between items-start">
                            <span class="label">{{ item.entity_label || item.entity_id }}</span>
                            <span class="timestamp">{{ formatTime(item.inserted_at) }}</span>
                        </div>
                        <div class="flex justify-between items-center mt-1">
                            <span class="sub-text font-mono">{{ formatTaxId(item.entity_id) }}</span>
                            <span v-if="showTypeIcon" class="type-tag" :class="item.entity_type">
                                {{ item.entity_type === 'person' ? 'Pessoa' : 'Empresa' }}
                            </span>
                            <span v-else class="date-text">{{ formatDate(item.inserted_at) }}</span>
                        </div>
                    </div>
                </router-link>
            </li>
        </ul>
    </div>
</template>

<style scoped>
.recent-activity-card {
    background: #fff;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid #f1f3f5;
}

.title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1a1d23;
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
}

.activity-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.activity-item {
    margin-bottom: 0.5rem;
}

.activity-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
    padding: 0.75rem;
    border-radius: 10px;
    transition: all 0.2s ease;
    border: 1px solid transparent;
}

.activity-link:hover {
    background-color: #f8f9fa;
    border-color: #e9ecef;
    transform: translateY(-1px);
}

.icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    margin-right: 1rem;
    flex-shrink: 0;
}

.icon-wrapper.company { background-color: #e7f1ff; color: #0d6efd; }
.icon-wrapper.person { background-color: #e6ffed; color: #28a745; }
.icon-wrapper.report { background-color: #f8f9fa; color: #6c757d; }

.info {
    min-width: 0;
}

.label {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2d3436;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 0.5rem;
}

.timestamp {
    font-size: 0.7rem;
    color: #adb5bd;
    font-weight: 500;
    flex-shrink: 0;
}

.sub-text {
    font-size: 0.8rem;
    color: #636e72;
}

.date-text {
    font-size: 0.75rem;
    color: #b2bec3;
}

.type-tag {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    padding: 2px 8px;
    border-radius: 4px;
    letter-spacing: 0.025em;
}

.type-tag.company { background: #e7f1ff; color: #0d6efd; }
.type-tag.person { background: #e6ffed; color: #28a745; }

.loading-state, .empty-state {
    text-align: center;
    padding: 2rem 1rem;
    color: #adb5bd;
}

.spin {
    animation: spin 1s linear infinite;
    font-size: 2rem;
}

@keyframes spin { 100% { transform: rotate(360deg); } }

.flex { display: flex; }
.flex-grow { flex-grow: 1; }
.justify-between { justify-content: space-between; }
.items-center { align-items: center; }
.items-start { align-items: flex-start; }
.mt-1 { margin-top: 0.25rem; }
.font-mono { font-family: monospace; }
</style>
